import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  ArrowRightIcon,
  Bars3BottomLeftIcon,
  ChartBarIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import BrandLogo from '../common/BrandLogo';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState(() => localStorage.getItem('sv_remembered_username') || '');
  const [password, setPassword] = useState(() => {
    localStorage.removeItem('sv_remembered_password');
    return '';
  });
  const [remember, setRemember] = useState(() => localStorage.getItem('sv_remember_me') === 'true');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [sessionNotice] = useState(() => {
    const notice = sessionStorage.getItem('sv_auth_notice');
    if (notice) {
      sessionStorage.removeItem('sv_auth_notice');
      return notice;
    }
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason');
    if (reason === 'account_expired') {
      return 'Tài khoản học viên của bạn đã hết hạn sử dụng. Vui lòng liên hệ Giáo vụ hoặc Trung tâm để gia hạn tài khoản và tiếp tục học tập!';
    }
    if (reason === 'account_locked') {
      return 'Tài khoản học viên của bạn đang bị khóa. Vui lòng liên hệ Quản trị viên Trung tâm để được hỗ trợ!';
    }
    return '';
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, currentUser } = useAuth();

  if (currentUser) return <Navigate to="/" />;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      if (remember) {
        localStorage.setItem('sv_remembered_username', email.trim());
        localStorage.removeItem('sv_remembered_password');
        localStorage.setItem('sv_remember_me', 'true');
      } else {
        localStorage.removeItem('sv_remembered_username');
        localStorage.removeItem('sv_remembered_password');
        localStorage.removeItem('sv_remember_me');
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Đăng nhập không thành công. Vui lòng kiểm tra lại thông tin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="sv-login-shell">
      {/* High-res original 3D background artwork */}
      <div className="sv-login-bg" aria-hidden="true" />
      {/* Soft luminous gradient overlay keeping text crystal clear without obscuring the character */}
      <div className="sv-login-overlay" aria-hidden="true" />

      {/* Topbar Header */}
      <header className="sv-login-topbar">
        <div className="sv-topbar-left">
          <BrandLogo className="sv-brand-logo" />
          <span className="sv-divider" />
          <div className="sv-program">
            <strong>Nền tảng đào tạo trực tuyến</strong>
            <span>Ôn thi chứng chỉ | Phát triển kỹ năng | Kiến tạo tương lai</span>
          </div>
        </div>
      </header>

      {/* Main Two-Column Stage */}
      <div className="sv-main-stage">
        {/* Left Side: Hero Title, Features, and Callout */}
        <section className="sv-hero-left" aria-label="Giới thiệu nền tảng ôn thi">
          <span className="sv-pill">Ôn thi chứng chỉ GenAI Sao Việt</span>

          <h1 className="sv-hero-title">
            Sẵn sàng chinh phục<br />
            chứng chỉ <span className="sv-genai-word">GenAI</span>
          </h1>

          <p className="sv-hero-sub">
            Luyện tập thông minh, thi thử sát thực tế, theo dõi tiến độ từng ngày.
          </p>

          <div className="sv-stats-row">
            <div className="sv-stat-card">
              <div className="sv-stat-icon purple">
                <ClipboardDocumentListIcon />
              </div>
              <strong>250 câu hỏi</strong>
              <span>chuẩn hóa</span>
            </div>

            <div className="sv-stat-card">
              <div className="sv-stat-icon orange">
                <Bars3BottomLeftIcon />
              </div>
              <strong>5 dạng bài</strong>
              <span>tương tác</span>
            </div>

            <div className="sv-stat-card">
              <div className="sv-stat-icon teal">
                <ChartBarIcon />
              </div>
              <strong>Theo dõi</strong>
              <span>tiến độ</span>
            </div>
          </div>

          <div className="sv-script-left">
            Kiến thức hôm nay<br />
            Cơ hội ngày mai
          </div>
        </section>

        {/* Right Side: Floating Script & White Form Card */}
        <section className="sv-hero-right" aria-label="Đăng nhập tài khoản">
          <div className="sv-script-top">
            Học hôm nay<br />
            Dẫn lối ngày mai
          </div>

          <div className="sv-form-card">
            <span className="sv-kicker">Chào mừng trở lại!</span>
            <h2 className="sv-card-title">Đăng nhập học tập</h2>
            <p className="sv-card-desc">
              Tiếp tục hành trình chinh phục chứng chỉ GenAI cùng Trung tâm Sao Việt.
            </p>

            {sessionNotice && !error && (
              <div
                className="sv-login-lock-notice"
                style={{
                  background: '#fff7ed',
                  border: '1px solid #fdba74',
                  color: '#9a3412',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  fontSize: '13.5px',
                  lineHeight: 1.5,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}
                role="alert"
              >
                <LockClosedIcon style={{ width: 22, height: 22, flexShrink: 0, marginTop: 2, color: '#ea580c' }} />
                <div>
                  <strong style={{ display: 'block', marginBottom: 2 }}>Thông báo tài khoản:</strong>
                  <span>{sessionNotice}</span>
                </div>
              </div>
            )}

            {error && (
              <div className="sv-login-error" role="alert">
                <ExclamationCircleIcon />
                <span>{error}</span>
              </div>
            )}

            <form className="sv-form-group" onSubmit={handleSubmit}>
              <label>
                <span>Tài khoản / Email đăng nhập</span>
                <div className="sv-input-field">
                  <EnvelopeIcon />
                  <input
                    autoComplete="username"
                    autoFocus={!email}
                    name="username"
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Mã tài khoản hoặc email"
                    required
                    type="text"
                    value={email}
                  />
                </div>
              </label>

              <label>
                <span>Mật khẩu</span>
                <div className="sv-input-field sv-password-field">
                  <LockClosedIcon />
                  <input
                    autoComplete="current-password"
                    name="password"
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Nhập mật khẩu"
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                  />
                  <button
                    aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                    className="sv-toggle-pwd"
                    onClick={() => setShowPassword((value) => !value)}
                    title={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                    type="button"
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </label>

              <div className="sv-row">
                <label className="sv-remember">
                  <input
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                    type="checkbox"
                  />
                  <span>Ghi nhớ đăng nhập</span>
                </label>
                <button
                  className="sv-forgot"
                  onClick={() => alert('Vui lòng liên hệ giáo vụ Trung tâm Tin học Sao Việt để được hỗ trợ cấp lại mật khẩu.')}
                  type="button"
                >
                  Quên mật khẩu?
                </button>
              </div>

              <button className="sv-submit" disabled={loading} type="submit">
                <LockClosedIcon />
                <span>{loading ? 'Đang xác thực...' : 'Đăng nhập'}</span>
                <ArrowRightIcon />
              </button>
            </form>

            <div className="sv-or"><span>hoặc</span></div>

            <a
              className="sv-support"
              href="https://tinhocsaoviet.com"
              rel="noopener noreferrer"
              target="_blank"
            >
              <svg
                aria-hidden="true"
                className="sv-headset-icon"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
              </svg>
              <div className="sv-support-text">
                <strong>Cần hỗ trợ?</strong>
                <small>Liên hệ giáo vụ Trung tâm Sao Việt</small>
              </div>
              <ChevronRightIcon className="sv-chevron-right" />
            </a>
          </div>

          <div className="sv-footer-text">Tri thức tạo nên giá trị thật</div>
        </section>
      </div>
    </main>
  );
};

export default Login;
